import { ColDef, ExcelExportParams, ExcelStyle, GridApi, exportMultipleSheetsAsExcel } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { InvoiceChargeType, InvoiceSummaryType, NotificationContextType } from "../types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { HStack, IconButton, Tab, TabList, TabPanel, TabPanels, Tabs, Tooltip } from "@chakra-ui/react";
import { NotificationContext } from "../components/NotificationPane";
import { useApi } from "../hooks/useApi";
import { caseInsensitiveComparator, dateValueFormatter, poundValueFormatter, timezoneIndependantDateComparator } from "../util/AgGridFunctions";
import { InvoiceFileButtonRenderer, InvoiceLinkRenderer } from "../components/CellRenderers";
import BaseTemplate from "../components/BaseTemplate"
import InternalUserRoute from "../util/InternalCustomerRoute";
import { invoicesDefaults } from "../util/PresetViews";
import PersistentGrid from "../components/PersistentGrid/PersistentGrid";
import { PersistentGridProvider } from "../contexts/PersistentGridContext";
import PersistentGridResetButton from "../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridViewSelector from "../components/PersistentGrid/PersistentGridViewSelector";
import { FaArrowsUpToLine, FaDownload, FaFilterCircleXmark } from "react-icons/fa6";


type FilterRow = { column: string; condition: string; value: string };

const filterSummaryColDefs: ColDef<FilterRow>[] = [
  { field: 'column', headerName: 'Column', width: 220 },
  { field: 'condition', headerName: 'Condition', width: 200 },
  { field: 'value', headerName: 'Value', width: 400 },
];

function formatConditions(model: any): { condition: string; value: string }[] {
  if (!model) return [];
  if (model.filterType === 'multi') {
    return (model.filterModels ?? []).flatMap((m: any) => formatConditions(m));
  }
  if (model.operator) {
    const c1 = formatConditions(model.condition1);
    const c2 = formatConditions(model.condition2);
    return c1.length && c2.length
      ? [...c1, { condition: model.operator, value: '' }, ...c2]
      : [...c1, ...c2];
  }
  if (model.filterType === 'set') {
    return [{ condition: 'In Set', value: (model.values ?? []).join(', ') }];
  }
  const typeLabels: Record<string, string> = {
    contains: 'Contains', notContains: 'Does Not Contain',
    equals: 'Equals', notEqual: 'Not Equal',
    startsWith: 'Starts With', endsWith: 'Ends With',
    greaterThan: 'Greater Than', greaterThanOrEqual: 'Greater Than or Equal',
    lessThan: 'Less Than', lessThanOrEqual: 'Less Than or Equal',
    inRange: 'In Range', blank: 'Is Blank', notBlank: 'Is Not Blank',
  };
  const label = typeLabels[model.type] ?? model.type;
  if (model.filterType === 'date') {
    const val = model.type === 'inRange'
      ? `${model.dateFrom?.split(' ')[0]} to ${model.dateTo?.split(' ')[0]}`
      : (model.dateFrom?.split(' ')[0] ?? '');
    return [{ condition: label, value: val }];
  }
  return [{ condition: label, value: model.filter?.toString() ?? '' }];
}

function buildFilterRows(filterModel: Record<string, any>, colDefs: ColDef[]): FilterRow[] {
  return Object.entries(filterModel).flatMap(([field, model]) => {
    const headerName = colDefs.find(c => c.field === field)?.headerName ?? field;
    return formatConditions(model).map(c => ({ column: headerName as string, ...c }));
  });
}

function Invoices() {
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
  const { ready, getInvoicesSummary, getInvoiceChargeDetail } = useApi();
  const [invoicesSummary, setInvoicesSummary] = useState<InvoiceSummaryType[] | undefined>(undefined);
  const [invoiceChargeDetail, setInvoiceChargeDetail] = useState<InvoiceChargeType[] | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (ready) loadInvoicesSummary();
  }, [ready, getInvoicesSummary]);

  useEffect(() => {
    if (ready && activeTab === 1 && invoiceChargeDetail === undefined) loadInvoiceChargeDetail();
  }, [ready, activeTab]);

  async function loadInvoicesSummary() {
    try {
      const responseJson = await getInvoicesSummary();
      setInvoicesSummary(responseJson);
    } catch {
      pushNotification("Could not load invoice summary.", "error")
    }
  }

  async function loadInvoiceChargeDetail() {
    try {
      const responseJson = await getInvoiceChargeDetail();
      setInvoiceChargeDetail(responseJson);
    } catch {
      pushNotification("Could not load invoice charge details.", "error")
    }
  }

  const [colDefs] = useState<ColDef<InvoiceSummaryType>[]>([
    {
      field: "filehash",
      headerName: "Invoice",
      width: 110,
      pinned: "left",
      sortable: true,
      filter: false,
      suppressMenu: true,
      cellRenderer: InvoiceFileButtonRenderer,
    },
    {
      field: "invoice_month", headerName: "Invoice Month", rowGroup: true, hide: true, sort: "desc", sortIndex: 0, valueGetter: (params) => params.data?.invoice_month,
      valueFormatter: (params) => (params.value) ? new Date(params.value).toLocaleString('default', { month: 'short', year: 'numeric' }) : ''
    },
    { field: "account_name", headerName: "Account Name", rowGroup: true, hide: true, sort: "asc", sortIndex: 1 },
    { field: "invoice_date", headerName: "Date", sort: "asc", width: 130, pinned: 'left', cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "supply_number", headerName: "Supply No.", headerTooltip: "Supply Number", sort: "asc", sortIndex: 2, width: 170, pinned: 'left' },
    { field: "invoice_number", headerName: "Invoice No.", cellRenderer: InvoiceLinkRenderer, width: 150, pinned: 'left', cellStyle: { overflow: "visible" } },
    { field: "supply_type_code", headerName: "Utility", width: 100, valueFormatter: (params) => (!params.value) ? "" : params.value.split('_').join(' ').toUpperCase() },
    { field: "period_start", headerName: "From", width: 130, sort: "asc", sortIndex: 3, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "period_end", headerName: "To", width: 130, sort: "asc", sortIndex: 4, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "supplier_code", headerName: "Supplier", width: 110 },
    { field: "subtotal_w_vat", headerName: "Subtotal (incl. VAT)", valueFormatter: poundValueFormatter, width: 140, cellClass: 'ag-right-aligned-cell' },
    { field: "supply_consumption", headerName: "Consumption", valueFormatter: poundValueFormatter, width: 145, cellClass: 'ag-right-aligned-cell' },
    { field: "standing", headerName: "Std. Charge", valueFormatter: poundValueFormatter, width: 130, cellClass: 'ag-right-aligned-cell' },
    { field: "supply_delivery", headerName: "Delivery", valueFormatter: poundValueFormatter, width: 130, cellClass: 'ag-right-aligned-cell' },
    { field: "metering", headerName: "Metering", valueFormatter: poundValueFormatter, width: 130, cellClass: 'ag-right-aligned-cell' },
    { field: "levy", headerName: "Levy", valueFormatter: poundValueFormatter, width: 130, cellClass: 'ag-right-aligned-cell' },
    { field: "finance", headerName: "Finance", valueFormatter: poundValueFormatter, width: 130, cellClass: 'ag-right-aligned-cell' },
    { field: "vat_total", headerName: "VAT Total", valueFormatter: poundValueFormatter, width: 130, cellClass: 'ag-right-aligned-cell' },
    { field: "bill_total", headerName: "Bill Total (All Supplies)", valueFormatter: poundValueFormatter, width: 140, cellClass: 'ag-right-aligned-cell' },
    { field: "validation", headerName: "Validation", width: 120 },
    { field: "message", headerName: "Message", width: 150 },
    { field: "is_estimate", headerName: "Est", headerTooltip: "Is estimate reading?", width: 100 },
  ]);

  const [chargeDetailColDefs] = useState<ColDef<InvoiceChargeType>[]>([
    { field: "supplier_code", headerName: "Supplier", width: 110, pinned: 'left' },
    { field: "account_name", headerName: "Account Name", width: 200 },
    { field: "account_id", headerName: "Account ID", width: 130 },
    { field: "site_id", headerName: "Site ID", width: 110 },
    { field: "supply_number", headerName: "Supply No.", headerTooltip: "Supply Number", width: 170 },
    { field: "invoice_number", headerName: "Invoice No.", width: 150 },
    { field: "invoice_date", headerName: "Invoice Date", width: 130, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "period_start", headerName: "Period Start", width: 130, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "period_end", headerName: "Period End", width: 130, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "supply_type_code", headerName: "Utility", width: 100, valueFormatter: (params) => params.value?.split('_').join(' ').toUpperCase() },
    { field: "charge_category", headerName: "Category", width: 140 },
    { field: "charge_name", headerName: "Charge Name", width: 180 },
    { field: "charge_description", headerName: "Description", width: 200 },
    { field: "charge_period_start", headerName: "Charge From", width: 130, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "charge_period_end", headerName: "Charge To", width: 130, cellDataType: "dateString", filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "measure_quantity", headerName: "Quantity", width: 120, cellClass: 'ag-right-aligned-cell' },
    { field: "measure_units", headerName: "Units", width: 100 },
    { field: "price", headerName: "Price", valueFormatter: poundValueFormatter, width: 110, cellClass: 'ag-right-aligned-cell' },
    { field: "charge_subtotal", headerName: "Subtotal", valueFormatter: poundValueFormatter, width: 120, cellClass: 'ag-right-aligned-cell' },
    { field: "validation", headerName: "Validation", width: 120 },
    { field: "message", headerName: "Message", width: 200 },
  ]);

  const defaultColDef: ColDef = useMemo(() => ({
    filter: 'agMultiColumnFilter',
    enableRowGroup: true,
    enableValue: true,
    comparator: caseInsensitiveComparator,
    floatingFilter: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }), []);

  const excelStyles: ExcelStyle[] = useMemo(() => ([
    {
      id: 'header',
      interior: { color: '#1e3a5f', pattern: 'Solid' },
      font: { color: '#ffffff', bold: true },
    },
  ]), []);

  const excelExportParams: ExcelExportParams = useMemo(() => ({
    freezeRows: 'headers',
    freezeColumns: 'pinned',
  }), []);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [chargeDetailGridApi, setChargeDetailGridApi] = useState<GridApi | null>(null);
  const [filterSummaryGridApi, setFilterSummaryGridApi] = useState<GridApi | null>(null);

  const exportWithFilterSheet = useCallback((
    dataApi: GridApi,
    gridColDefs: ColDef[],
    dataSheetName: string,
    fileName: string,
  ) => {
    const filterModel = dataApi.getFilterModel();
    const mainSheet = dataApi.getSheetDataForExcel({ ...excelExportParams, sheetName: dataSheetName })!;
    const filterRows = buildFilterRows(filterModel, gridColDefs);

    if (filterRows.length === 0 || !filterSummaryGridApi) {
      exportMultipleSheetsAsExcel({ data: [mainSheet], fileName });
      return;
    }

    filterSummaryGridApi.setGridOption('rowData', filterRows);
    const filterSheet = filterSummaryGridApi.getSheetDataForExcel({ sheetName: 'Filters Applied' })!;
    exportMultipleSheetsAsExcel({ data: [mainSheet, filterSheet], fileName });
  }, [excelExportParams, filterSummaryGridApi]);

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey={"invoices"} views={invoicesDefaults}>
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <HStack>
              {activeTab === 0 && (
                <>
                  <PersistentGridResetButton />
                  <Tooltip label="Clear Filters" hasArrow>
                    <IconButton
                      icon={<FaFilterCircleXmark />}
                      onClick={() => gridApi?.setFilterModel(null)}
                      aria-label="Clear Filters"
                    />
                  </Tooltip>
                  <IconButton icon={<FaArrowsUpToLine />} onClick={() => gridApi?.collapseAll()} aria-label={"Collapse All"}>Collapse All</IconButton>
                  <Tooltip label="Export to Excel" hasArrow>
                    <IconButton
                      icon={<FaDownload />}
                      onClick={() => gridApi && exportWithFilterSheet(gridApi, colDefs, 'Invoice Summary', 'Invoice Summary.xlsx')}
                      aria-label="Export to Excel"
                    />
                  </Tooltip>
                  <PersistentGridViewSelector />
                </>
              )}
              {activeTab === 1 && (
                <>
                  <Tooltip label="Clear Filters" hasArrow>
                    <IconButton
                      icon={<FaFilterCircleXmark />}
                      onClick={() => chargeDetailGridApi?.setFilterModel(null)}
                      aria-label="Clear Filters"
                    />
                  </Tooltip>
                  <Tooltip label="Export to Excel" hasArrow>
                    <IconButton
                      icon={<FaDownload />}
                      onClick={() => chargeDetailGridApi && exportWithFilterSheet(chargeDetailGridApi, chargeDetailColDefs, 'Charge Details', 'Charge Details.xlsx')}
                      aria-label="Export to Excel"
                    />
                  </Tooltip>
                </>
              )}
            </HStack>
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Tabs colorScheme='purple' isLazy variant='enclosed' h="100%" onChange={(index) => setActiveTab(index)}>
              <TabList>
                <Tab>Invoice Summary</Tab>
                <Tab>Charge Details</Tab>
              </TabList>
              <TabPanels h="90%">
                <TabPanel h="100%" p={0} pt={2}>
                  <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                    <PersistentGrid<InvoiceSummaryType>
                      onGridReady={params => setGridApi(params.api)}
                      rowData={invoicesSummary}
                      columnDefs={colDefs}
                      defaultColDef={defaultColDef}
                      rowGroupPanelShow={"always"}
                      groupDisplayType={'groupRows'}
                      groupDefaultExpanded={0}
                      suppressRowTransform
                      suppressCsvExport={true}
                      excelStyles={excelStyles}
                      defaultExcelExportParams={excelExportParams}
                      headerHeight={32}
                      rowHeight={32}
                      floatingFiltersHeight={32}
                    />
                  </div>
                </TabPanel>
                <TabPanel h="100%" p={0} pt={2}>
                  <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                    <AgGridReact<InvoiceChargeType>
                      onGridReady={params => setChargeDetailGridApi(params.api)}
                      rowData={invoiceChargeDetail}
                      columnDefs={chargeDetailColDefs}
                      defaultColDef={defaultColDef}
                      suppressCsvExport={true}
                      excelStyles={excelStyles}
                      defaultExcelExportParams={excelExportParams}
                      headerHeight={32}
                      rowHeight={32}
                      floatingFiltersHeight={32}
                    />
                  </div>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </BaseTemplate.Body>
        </BaseTemplate>
        <div className="ag-theme-quartz" style={{ height: 0, overflow: 'hidden' }}>
          <AgGridReact<FilterRow>
            onGridReady={params => setFilterSummaryGridApi(params.api)}
            columnDefs={filterSummaryColDefs}
            rowData={[]}
          />
        </div>
      </PersistentGridProvider>
    </InternalUserRoute>
  )
}

export default Invoices
